// Render de cupom em texto puro ASCII pra impressoras sem driver real
// (ex: "Generic / Text Only" do Windows). O Print Agent envia esse texto
// via `printDirect({ type: 'TEXT' })` — o driver renderiza com a fonte
// interna do Windows usando o codepage default da máquina.
//
// Limitações vs ESC/POS: sem negrito, sem fonte grande, sem corte
// automático, sem alinhamento à direita real (usamos padding com espaço),
// e SEM acento (transliterado pra ASCII — driver Text-Only usa CP437
// onde "ção" sai como lixo). Os tradeoffs valem porque a alternativa é
// não imprimir nada.

import { getCharsPerLine, type PaperWidth } from "./thermal-printer";
import { linhasDoRodape } from "./receipt-footer";
import { groupOptionsByTitle, receiptGroupTitle, receiptOrderLabel, receiptModeLabel, receiptItemLabel, receiptPrecoNaOpcao, receiptPrecoDaOpcao, receiptCashDue, receiptWhatsapp, receiptCrediarioLine, receiptTopMarginLines, receiptShowsPaidCheckbox, birthdayMessageLines, storeNameHeader, receiptDataUnica, receiptQuandoEncomenda, linhasDeOpcao, receiptLoyaltyDiscount, receiptWeight, taxaDeServico, PAID_CHECKBOX_LABEL, type ReceiptOrder } from "./receipt-formatter";

function formatCurrency(value: number): string {
  return `R$ ${value.toFixed(2).replace(".", ",")}`;
}

function formatDate(dateStr: string, timezone?: string | null): string {
  return new Date(dateStr)
    .toLocaleString("pt-BR", {
      timeZone: timezone || "America/Sao_Paulo",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
    .replace(", ", " ");
}

// Modo raster: MANTÉM o acento Latin-1 em vez de transliterar — a renderização
// vira imagem (GS v 0, glifos desenhados por nós), então não depende da fonte
// interna da impressora. Set/reset síncrono em formatReceiptLinesAccented (não vaza).
let keepAccents = false;

// Strip acentos via NFD + remove combining marks. Caracteres fora do range
// imprimível ASCII viram "?" (raro — apenas emojis e símbolos exóticos).
export function asciiReceiptText(str: string): string {
  return ascii(str);
}

function ascii(str: string): string {
  if (keepAccents) {
    // Mantém ASCII + Latin-1 (0xA1-0xFF, onde vivem os acentos); normaliza a
    // pontuação tipográfica comum pra ASCII; o resto (emoji, CJK) vira "?".
    return str
      .normalize("NFC")
      .replace(/[‘’‚]/g, "'")
      .replace(/[“”„]/g, '"')
      .replace(/[–—―•]/g, "-")
      .replace(/…/g, "...")
      .replace(/ /g, " ")
      .replace(/[^\x20-\x7E¡-ÿ\n\r\t]/g, "?");
  }
  return str
    // "8½ preparos" → "8 1/2" (½ não é ASCII; sem isso virava "8?").
    .replace(/(\d)½/g, "$1 1/2")
    .replace(/½/g, "1/2")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^\x20-\x7E\n\r\t]/g, "?");
}

// Retorna 1+ linhas. Versão anterior fatiava o `left` com .slice quando o
// conteúdo não cabia (caso real Fênix Burger jun/2026: "1x 3 X-bacon
// Especial + Tuchaua 1,5l" virou "1x 3 X-bacon Especial +" no cupom 58mm).
// Agora: quando não cabe, quebra o nome em palavras (via `wrap`) e tenta
// encaixar o `right` na última linha; se não couber, manda linha extra com
// o `right` alinhado à direita.
function pad(left: string, right: string, cols: number): string[] {
  const l = ascii(left);
  const r = ascii(right);
  if (l.length + 1 + r.length <= cols) {
    const space = cols - l.length - r.length;
    return [l + " ".repeat(space) + r];
  }
  const wrapped = wrap(l, cols);
  const last = wrapped[wrapped.length - 1];
  if (last.length + 1 + r.length <= cols) {
    const space = cols - last.length - r.length;
    wrapped[wrapped.length - 1] = last + " ".repeat(space) + r;
    return wrapped;
  }
  const rightPad = Math.max(0, cols - r.length);
  wrapped.push(" ".repeat(rightPad) + r);
  return wrapped;
}

function center(str: string, cols: number): string {
  const a = ascii(str);
  if (a.length >= cols) return a.slice(0, cols);
  const padLeft = Math.floor((cols - a.length) / 2);
  return " ".repeat(padLeft) + a;
}

function wrap(str: string, cols: number): string[] {
  const a = ascii(str);
  if (a.length <= cols) return [a];
  // Quebra preferencialmente em espaços.
  const out: string[] = [];
  const words = a.split(" ");
  let current = "";
  for (const w of words) {
    if (w.length > cols) {
      if (current) { out.push(current); current = ""; }
      for (let i = 0; i < w.length; i += cols) out.push(w.slice(i, i + cols));
      continue;
    }
    if ((current + " " + w).trim().length > cols) {
      out.push(current);
      current = w;
    } else {
      current = current ? `${current} ${w}` : w;
    }
  }
  if (current) out.push(current);
  return out;
}

// Linha do cupom com marcação de estilo. `big` = tamanho dobrado (nome da loja,
// TOTAL); `bold` = negrito. O renderizador raster desenha o estilo; o texto puro
// (driver Text-Only) ignora — só usa `.text`.
export type StyledLine = { text: string; big?: boolean; bold?: boolean };

// Monta as linhas do cupom com estilo. Layout único, compartilhado pelo texto
// puro e pelo raster. `bigCols`: largura usada nas linhas GRANDES (nome da loja
// e TOTAL) — no texto = `cols` (mesma coisa de sempre); no raster = metade, pois
// elas são desenhadas em 2x e aí preenchem o papel inteiro.
function buildReceiptLines(
  storeName: string,
  order: ReceiptOrder,
  width: PaperWidth,
  cols: number,
  bigCols: number,
): StyledLine[] {
  const topLeft = receiptOrderLabel(order);
  const sep = "-".repeat(cols);

  const lines: StyledLine[] = [];
  const N = (text: string) => lines.push({ text });
  const B = (text: string) => lines.push({ text, bold: true });
  const BIG = (text: string) => lines.push({ text, big: true, bold: true });

  for (let i = 0; i < receiptTopMarginLines(order); i++) N("");

  const header = storeNameHeader(ascii(storeName), cols, bigCols);
  for (const ln of header.lines) {
    if (header.big) BIG(center(ln, bigCols));
    else B(center(ln, cols));
  }
  N("");
  N(sep);

  for (const t of pad(topLeft, formatDate(order.createdAt, order.timezone), cols)) B(t);
  if (order.tableNumber) BIG(`${order.tableLabel || "MESA"} ${ascii(order.tableNumber)}`);
  if (order.tableNumber && order.mesaDoCliente) BIG(`MESA ${ascii(order.mesaDoCliente)}`);
  N(`Cliente: ${ascii(order.customerName)}`);
  const wpp = order.customerWhatsapp ?? receiptWhatsapp((order as { customer?: { whatsappNumber?: string } }).customer?.whatsappNumber);
  if (wpp) N(`WhatsApp: ${wpp}`);
  // Depois do WhatsApp do comprador (ver receipt-formatter.ts).
  if (order.giftRecipient) B(ascii(`Presente para: ${order.giftRecipient}`));
  const dataUnica = receiptDataUnica(order.items);
  if (dataUnica) B(ascii(`Encomenda: ${dataUnica.data}${receiptQuandoEncomenda(dataUnica.hora, dataUnica.turno)}`));
  N(sep);

  for (const [idx, item] of order.items.entries()) {
    if (order.semValores || receiptPrecoNaOpcao(item)) for (const t of wrap(ascii(receiptItemLabel(item)), cols)) B(t);
    else for (const t of pad(ascii(receiptItemLabel(item)), formatCurrency(item.subtotal), cols)) B(t);

    if (item.weightKg != null && item.weightKg > 0) {
      N(`   Peso: ${receiptWeight(item.weightKg, item.weightUnit)}`);
    }

    if (!order.semValores && item.unitPrice !== item.subtotal / item.quantity) {
      N(`   Un: ${formatCurrency(item.unitPrice)}`);
    }

    if (item.prepLine) for (const l of item.prepLine.split("\n")) N(`   ${ascii(l)}`);

    let hadGroupTitle = false;
    if (item.options && item.options.length > 0) {
      for (const grp of groupOptionsByTitle(item.options, item.quantity)) {
        if (grp.title) {
          hadGroupTitle = true;
          N("");
          B(`   ${ascii(receiptGroupTitle(item, grp.title))}`);
        }
        for (const g of grp.options) {
          const priceStr = !order.semValores && g.price > 0 ? receiptPrecoDaOpcao(item, g.price * g.count) : "";
          const nomeOpc = `${g.count > 1 ? `${g.count}x ` : ""}${ascii(g.name)}`;
          if (`   + ${nomeOpc}${priceStr}`.length > cols) {
            for (const l of linhasDeOpcao(nomeOpc, priceStr, cols)) N(l);
          } else N(`   + ${nomeOpc}${priceStr}`);
        }
      }
    }

    if (item.removedIngredients && item.removedIngredients.length > 0) {
      N(`   Sem: ${ascii(item.removedIngredients.join(", "))}`);
    }

    if (item.scheduledDate && !dataUnica) {
      const [, m, d] = item.scheduledDate.split("-");
      N(ascii(`   Encomenda: ${d}/${m}${receiptQuandoEncomenda(item.scheduledTime, item.scheduledShift)}`));
    }

    if (item.notes) {
      for (const t of wrap(`   Obs: ${item.notes}`, cols)) N(t);
    }

    // Uma linha em branco entre os itens (T-0003, opção A); depois do último, só o respiro do item com títulos.
    if (idx < order.items.length - 1 || hadGroupTitle) N("");
  }

  if (!order.semValores) {
    N(sep);
    for (const t of pad("Subtotal:", formatCurrency(order.subtotal), cols)) N(t);

    const couponNum = order.couponDiscount != null
      ? (typeof order.couponDiscount === "number" ? order.couponDiscount : parseFloat(String(order.couponDiscount)))
      : 0;
    if (!isNaN(couponNum) && couponNum > 0) {
      const label = order.couponCode ? `Cupom (${order.couponCode}):` : "Cupom:";
      for (const t of pad(label, `-${formatCurrency(couponNum)}`, cols)) N(t);
    }

    const loyaltyNum = receiptLoyaltyDiscount(order);
    if (loyaltyNum > 0) {
      for (const t of pad(order.loyaltyLabel ? `Pontos (${order.loyaltyLabel}):` : "Pontos:", `-${formatCurrency(loyaltyNum)}`, cols)) N(t);
    }

    if (order.deliveryMode === "entrega") {
      const feeLabel = order.deliveryFeePending
        ? "a calcular"
        : order.deliveryFee > 0 ? formatCurrency(order.deliveryFee) : "Grátis";
      for (const t of pad("Entrega:", feeLabel, cols)) N(t);
    }

    const surchargeNum = order.cardSurcharge != null
      ? (typeof order.cardSurcharge === "number" ? order.cardSurcharge : parseFloat(String(order.cardSurcharge)))
      : 0;
    if (!isNaN(surchargeNum) && surchargeNum > 0) {
      for (const t of pad(`Acréscimo (${order.paymentMethod}):`, formatCurrency(surchargeNum), cols)) N(t);
    }
    const servico = taxaDeServico(order);
    if (servico) for (const t of pad(`${servico.rotulo}:`, formatCurrency(servico.valor), cols)) N(t);

    N("");
    N(sep);
    for (const t of pad("TOTAL:", formatCurrency(order.total), bigCols)) BIG(t);
    if (receiptShowsPaidCheckbox(order)) {
      for (const t of pad("", PAID_CHECKBOX_LABEL, cols)) B(t);
    }
    N(sep);

    N(`Pagamento: ${ascii(order.paymentMethod)}`);
    if (Array.isArray(order.paymentParts) && order.paymentParts.length > 0) {
      for (const pp of order.paymentParts) {
        N(`  ${ascii(pp.method)}: ${formatCurrency(Number(pp.amount) || 0)}`);
      }
    }

    const hasCash = order.paymentMethod === "Dinheiro" ||
      (Array.isArray(order.paymentParts) && order.paymentParts.some((p) => p.method === "Dinheiro"));
    if (hasCash) {
      const cashSource = order.changeFor != null && order.changeFor !== ""
        ? order.changeFor
        : (order.cashReceived != null && order.cashReceived !== "" ? order.cashReceived : null);
      if (order.needsChange === false) {
        N(ascii("Troco: Não precisa"));
      } else if (cashSource != null) {
        const cashNum = typeof cashSource === "number" ? cashSource : parseFloat(String(cashSource));
        if (!isNaN(cashNum) && cashNum > 0) {
          N(`Troco para: ${formatCurrency(cashNum)}`);
          const diff = Math.round((cashNum - receiptCashDue(order)) * 100) / 100;
          if (diff > 0) B(`Troco a devolver: ${formatCurrency(diff)}`);
        } else {
          N(`Troco para: ${ascii(String(cashSource))}`);
        }
      }
    }

    const credLine = receiptCrediarioLine(order);
    if (credLine) N(ascii(credLine));

    N(`Modalidade: ${receiptModeLabel(order)}${order.pickupMethod ? ` - ${ascii(order.pickupMethod)}` : ""}`);
  }

  if (order.deliveryMode === "entrega" && order.address) {
    const a = order.address;
    let addrLine = [a.street, a.number].filter(Boolean).join(", ");
    if (a.complement) addrLine += ` - ${a.complement}`;
    for (const t of wrap(`End: ${addrLine}`, cols)) N(t);
    const addrLine2 = [a.neighborhood, a.city].filter(Boolean).join(" - ");
    if (addrLine2) N(`     ${ascii(addrLine2)}`);
    if (a.zipCode) N(`     CEP: ${a.zipCode}`);
    if (a.reference) for (const t of wrap(`Ref: ${a.reference}`, cols)) N(t);
  }

  if (order.notes) {
    N(sep);
    B(ascii("Observações:"));
    for (const t of wrap(order.notes, cols)) N(t);
  }

  if (order.birthdayMessage) {
    // Moldura de asteriscos no lugar do separador tracejado, com respiro
    // depois da última linha do pedido.
    N("");
    N("*".repeat(cols));
    for (const parte of birthdayMessageLines(ascii(order.birthdayMessage), order.birthdayName ? ascii(order.birthdayName) : null, cols)) {
      for (const t of wrap(parte, cols)) N(center(t, cols));
    }
    N("*".repeat(cols));
    N("");
  } else {
    N(sep);
    N("");
  }
  if (order.footerMessage) {
    // A mensagem da loja no lugar do Obrigado. Na Imagem sai como a loja escreveu; no Simples o acento cai como no resto
    // do cupom e º ª ° viram o, a, o (o ½ o ascii já troca por 1/2).
    const rodape = keepAccents ? order.footerMessage : order.footerMessage.replace(/º/g, "o").replace(/ª/g, "a").replace(/°/g, "o");
    for (const t of linhasDoRodape(ascii(rodape), cols)) N(t ? center(t, cols) : "");
  } else {
    N(center("Obrigado pela preferência!", cols));
  }
  // 5 line-feeds pra alimentar papel suficiente pro lojista cortar à mão
  // (driver Text-Only não suporta corte automático).
  N("");
  N("");
  N("");
  N("");
  N("");

  return lines;
}

export function formatReceiptText(
  storeName: string,
  order: ReceiptOrder,
  width: PaperWidth,
  colsOverride?: number,
): string {
  // colsOverride: o modo raster usa fonte mais larga (menos colunas) — o layout
  // precisa quebrar nas mesmas colunas em que renderiza, senão trunca o preço.
  // bigCols = cols aqui: no texto puro não existe "grande", o estilo é ignorado.
  const cols = colsOverride ?? getCharsPerLine(width);
  // Usa CRLF — drivers Windows Text-Only esperam CR+LF como quebra de linha.
  return buildReceiptLines(storeName, order, width, cols, cols)
    .map((l) => l.text)
    .join("\r\n") + "\r\n";
}

/**
 * Linhas COM estilo (negrito / grande) e acento Latin-1 mantido — pro raster,
 * que desenha os glifos e aplica o estilo. Nome da loja e TOTAL são `big`
 * (renderizados em 2x), por isso laidos em metade das colunas pra preencher o
 * papel quando dobrados.
 */
export function formatReceiptStyledLines(
  storeName: string,
  order: ReceiptOrder,
  width: PaperWidth,
  cols: number,
): StyledLine[] {
  keepAccents = true;
  try {
    return buildReceiptLines(storeName, order, width, cols, Math.floor(cols / 2));
  } finally {
    keepAccents = false;
  }
}

/**
 * Mesmas linhas do cupom (mesmo layout/colunas), mas MANTENDO o acento Latin-1
 * — pra renderização raster (imagem), onde os glifos são desenhados por nós.
 * Retorna array de linhas (sem o CRLF).
 */
export function formatReceiptLinesAccented(
  storeName: string,
  order: ReceiptOrder,
  width: PaperWidth,
  cols?: number,
): string[] {
  keepAccents = true;
  try {
    return formatReceiptText(storeName, order, width, cols).split("\r\n");
  } finally {
    keepAccents = false;
  }
}
